import { invoiceServices } from "@services/apis/Invoice";
import { GetUnserInvoiceBankData } from "@services/models/InvoiceApprovals/GetUnserInvoiceBankModel";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import AgGridReactTable from "../../components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "../../components/ag-grid/AgGridSkeleton";
import CopyButton from "../../components/buttons/CopyButton";
import StatusPillBankManagement from "./components/StatusPillBankManagement";

const BankInvoiceUsers = () => {
  const gridRef = useRef<AgGridReact>(null);

  const [accountData, setAccountData] = useState<GetUnserInvoiceBankData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Account ID",
        field: "accountId",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 150,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "User Email",
        field: "userEmail",
        flex: 2,
        minWidth: 200,
        maxWidth: 300,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2 h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value}
              </span>
              <CopyButton
                copyText={params.value}
                copyMessage="Email copied!"
                copyIconSize={10}
                buttonSize="16px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Account Holder",
        field: "accountHolderFullName",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 250,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "IBAN",
        field: "iban",
        flex: 2,
        minWidth: 180,
        maxWidth: 280,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          if (!params.value) return <span className="text-text-subtle">-</span>;
          return (
            <div className="flex items-center gap-2 h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value}
              </span>
              <CopyButton
                copyText={params.value}
                copyMessage="IBAN copied!"
                copyIconSize={10}
                buttonSize="16px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Account Number",
        field: "accountNumber",
        flex: 1.5,
        minWidth: 150,
        maxWidth: 200,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          if (!params.value) return <span className="text-text-subtle">-</span>;
          return (
            <div className="flex items-center gap-2 h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.value}
              </span>
              <CopyButton
                copyText={params.value}
                copyMessage="Account number copied!"
                copyIconSize={10}
                buttonSize="16px"
              />
            </div>
          );
        },
      },
      {
        headerName: "Country",
        field: "country",
        flex: 0.8,
        minWidth: 80,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return params.value || <span className="text-text-subtle">-</span>;
        },
      },
      {
        headerName: "Status",
        field: "isActive",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          return (
            <StatusPillBankManagement
              statusText={params.data.isActive ? "ACTIVE" : "INACTIVE"}
            />
          );
        },
      },
    ],
    []
  );

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const response = await invoiceServices.getUserInvoiceBankAccounts();
      setAccountData(response.data.accounts);
    } catch (err: any) {
      setError("Failed to fetch user invoice bank accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountData();
  }, []);

  return (
    <div className="flex flex-col bg-bg-page-body py-[20px] h-[85vh]">
      {loading ? (
        <AgGridSkeleton
          columnDefs={columnDefs}
          style={{ height: "calc(100vh - 110px)", width: "100%" }}
        />
      ) : (
        <AgGridReactTable
          ref={gridRef}
          rowData={accountData}
          columnDefs={columnDefs}
          getRowId={(params) => params.data.accountId?.toString()}
          error={error}
          style={{ height: "calc(100vh - 110px)", width: "100%" }}
        />
      )}
    </div>
  );
};

export default BankInvoiceUsers;
